import _pt from "prop-types";

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React from 'react';
import vegaEmbed from 'vega-embed';
import shallowEqual from './utils/shallowEqual';
import getUniqueFieldNames from './utils/getUniqueFieldNames';
import { NOOP } from './constants';
import addSignalListenersToView from './utils/addSignalListenersToView';
import computeSpecChanges from './utils/computeSpecChanges';
import removeSignalListenersFromView from './utils/removeSignalListenersFromView';
export function combineSpecWithDimension(props) {
  const {
    spec,
    width,
    height
  } = props;

  if (typeof width !== 'undefined' && typeof height !== 'undefined') {
    return _extends({}, spec, {
      width,
      height
    });
  }

  if (typeof width !== 'undefined') {
    return _extends({}, spec, {
      width
    });
  }

  if (typeof height !== 'undefined') {
    return _extends({}, spec, {
      height
    });
  }

  return spec;
}
export default class VegaEmbed extends React.PureComponent {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "containerRef", React.createRef());

    _defineProperty(this, "viewPromise", void 0);

    _defineProperty(this, "handleError", error => {
      const {
        onError = NOOP
      } = this.props;
      onError(error); // eslint-disable-next-line no-console

      console.warn(error);
      return undefined;
    });

    _defineProperty(this, "modifyView", action => {
      if (this.viewPromise) {
        this.viewPromise.then(view => {
          if (view) {
            action(view);
          }

          return true;
        }).catch(this.handleError);
      }
    });
  }

  componentDidMount() {
    this.createView();
  }

  componentDidUpdate(prevProps) {
    const fieldSet = getUniqueFieldNames([this.props, prevProps]);
    fieldSet.delete('className');
    fieldSet.delete('signalListeners');
    fieldSet.delete('spec');
    fieldSet.delete('style');
    fieldSet.delete('width');
    fieldSet.delete('height'); // Only create a new view if necessary

    if (!this.viewPromise || Array.from(fieldSet).some(f => this.props[f] !== prevProps[f])) {
      this.clearView();
      this.createView();
    } else {
      const specChanges = computeSpecChanges(combineSpecWithDimension(this.props), combineSpecWithDimension(prevProps));
      const {
        signalListeners: newSignalListeners
      } = this.props;
      const {
        signalListeners: oldSignalListeners
      } = prevProps;

      if (specChanges) {
        if (specChanges.isExpensive) {
          this.clearView();
          this.createView();
        } else {
          const areSignalListenersChanged = !shallowEqual(newSignalListeners, oldSignalListeners);
          this.modifyView(view => {
            if (specChanges.width !== false) {
              view.width(specChanges.width);
            }

            if (specChanges.height !== false) {
              view.height(specChanges.height);
            }

            if (areSignalListenersChanged) {
              if (oldSignalListeners) {
                removeSignalListenersFromView(view, oldSignalListeners);
              }

              if (newSignalListeners) {
                addSignalListenersToView(view, newSignalListeners);
              }
            }

            view.run();
          });
        }
      } else if (!shallowEqual(newSignalListeners, oldSignalListeners)) {
        this.modifyView(view => {
          if (oldSignalListeners) {
            removeSignalListenersFromView(view, oldSignalListeners);
          }

          if (newSignalListeners) {
            addSignalListenersToView(view, newSignalListeners);
          }

          view.run();
        });
      }
    }
  }

  componentWillUnmount() {
    this.clearView();
  }

  createView() {
    const _this$props = this.props,
          {
      onNewView,
      signalListeners = {}
    } = _this$props,
          options = _objectWithoutPropertiesLoose(_this$props, ["spec", "onNewView", "signalListeners"]);

    if (this.containerRef.current) {
      const finalSpec = combineSpecWithDimension(this.props);
      this.viewPromise = vegaEmbed(this.containerRef.current, finalSpec, options).then(({
        view
      }) => {
        if (addSignalListenersToView(view, signalListeners)) {
          view.run();
        }

        return view;
      }).catch(this.handleError);

      if (onNewView) {
        this.modifyView(onNewView);
      }
    }
  }

  clearView() {
    this.modifyView(view => {
      view.finalize();
    });
    this.viewPromise = undefined;
    return this;
  }

  render() {
    const {
      className,
      style
    } = this.props; // Create the container Vega draws inside

    return /*#__PURE__*/React.createElement("div", {
      ref: this.containerRef,
      className: className,
      style: style
    });
  }

}

_defineProperty(VegaEmbed, "propTypes", {
  className: _pt.string,
  onError: _pt.func
});